import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider} from '@apps-common/auth-react';
import {DocumentHandler} from '@apps-common/auth-utils';
import {setAuthToken} from '@apps-common/js-client/api/request';
import {CssBaseline, ThemeProvider} from '@mui/material';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import React, {useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {Provider} from 'react-redux';
import {useNavigate} from 'react-router';
import appGlobalStyles from '../AppGlobalStyles.jsx';
import {setView as setMapView} from '../actions/map.js';
import {identify} from '../analytics.js';
import Pages from '../pages/index.jsx';
import store from '../store.js';
import {theme} from '../styles.js';
import pluckQueryParams from '../util/pluck-query-params.js';

export const queryClient = new QueryClient();

let {center, zoom} = pluckQueryParams(['center', 'zoom']);
if (center) {
  center = center.split(',').map(coord => Number(coord));
  if (center.length === 2 && !center.some(isNaN) && zoom) {
    zoom = Number(zoom);
    if (!isNaN(zoom)) {
      store.dispatch(setMapView({center, zoom}));
    }
  }
}

const basePath = import.meta.env.BASE_URL || '/';
const authClient = new AuthClient({
  clientId:
    import.meta.env.VITE_OIDC_CLIENT_ID || 'P1ZUoRvAQKJnFFheG3bBClkeZGum8unM',
  redirectUri: `${window.location.origin}${basePath}callback/`,
  postLogoutRedirectUri: `${window.location.origin}${basePath}`,
});

const App = () => {
  const navigate = useNavigate();
  const ldClient = useLDClient();

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    state => {
      setAuthToken(state.user?.accessToken || '');

      if (state.user && !state.pending) {
        const {
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
          sh_user_id,
          pl_project,
          pl_workspace,
        } = state.user.claims ?? {};

        identify(state.user.claims);

        if (user_id && organization_id) {
          ldClient.identify({
            kind: 'multi',
            user: {
              key: `user-${user_id}`,
              email,
              role_level,
              user_id,
              sh_user_id,
              pl_project,
              pl_workspace,
              _meta: {
                privateAttributes: ['email'],
              },
            },
            organization: {
              key: `organization-${organization_id}`,
              organization_id,
              organization_name,
              program_id,
              _meta: {
                privateAttributes: ['organization_name'],
              },
            },
          });
        }
      }
    },
    [ldClient],
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      onChange={onAuthChange}
    >
      <ThemeProvider defaultMode="dark" theme={theme}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <CssBaseline />

            {appGlobalStyles}

            <Helmet
              defaultTitle="Planet Stories"
              titleTemplate="%s - Planet Stories"
            />
            <DocumentHandler>
              <Pages />
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            </DocumentHandler>
          </QueryClientProvider>
        </Provider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
